<section
  class="canvas-shell"
  [class.canvas-shell--collapsed]="sidebarCollapsed()"
  [class.canvas--group-dragging]="groupDragActive()"
  cdkDropListGroup
  [class.canvas-shell--local-drop-active]="localFileDropActive()"
>
  <aside class="sidebar" [class.sidebar--collapsed]="sidebarCollapsed()">
    <div class="sidebar__header">
      <div>
        <h2>{{ 'canvas_documents_title_tr' | translate }}</h2>
        <p>{{ 'canvas_documents_hint_tr' | translate }}</p>
      </div>
      <button
        mat-icon-button
        type="button"
        class="sidebar__collapse"
        [matTooltip]="'canvas_hide_documents_tr' | translate"
        [attr.aria-label]="'canvas_hide_documents_tr' | translate"
        (click)="toggleSidebar()"
      >
        <mat-icon>chevron_left</mat-icon>
      </button>
    </div>
    <div class="sidebar__source">
      <app-document-list-table
        dragDropListId="canvas-source-list"
        [dragDropConnections]="['canvas-stage-list']"
        mode="uploaded"
        [documents]="sourceDocuments()"
        [previewMap]="getPreviewMap()"
        [total]="sourceTotalElements()"
        [pageIndex]="sourcePageIndex()"
        [pageSize]="sourcePageSize()"
        [sortField]="sourceSortField()"
        [sortDirection]="sourceSortDirection()"
        [hideActions]="true"
        [dragEnabled]="true"
        [usagePresentation]="'count'"
        [openUsageInNewTab]="true"
        [highlightedIds]="placedDocumentIds()"
        [rowTooltip]="documentRowTooltip"
        [emptyTitle]="'No_uploaded_documents_found_tr' | translate"
        [emptySubtitle]="'Manage_uploaded_documents_tr' | translate"
        emptyStateTestId="document-manager-canvas-empty"
        [disableActions]="sourceLoading()"
        (sortChange)="handleSourceSortChange($event)"
        (pageChange)="handleSourcePageChange($event)"
        (rowDragMoved)="handleSourceRowDragMove($event)"
        (navigateUsage)="handleNavigateUsage($event)"
      />
    </div>
  </aside>
  <main class="canvas">
    <header class="canvas__header">
      <div class="canvas__title-block">
        <div class="canvas__title-row">
          @if (sidebarCollapsed()) {
            <button
              mat-icon-button
              type="button"
              class="canvas__sidebar-reopen"
              [matTooltip]="'canvas_show_documents_tr' | translate"
              [attr.aria-label]="'canvas_show_documents_tr' | translate"
              (click)="toggleSidebar()"
            >
              <mat-icon class="canvas__desktop-sidebar-icon">chevron_right</mat-icon>
              <mat-icon class="canvas__mobile-sidebar-icon">expand_less</mat-icon>
            </button>
          } @else {
            <button
              mat-icon-button
              type="button"
              class="canvas__mobile-sidebar-toggle"
              [matTooltip]="'canvas_hide_documents_tr' | translate"
              [attr.aria-label]="'canvas_hide_documents_tr' | translate"
              (click)="toggleSidebar()"
            >
              <mat-icon>expand_more</mat-icon>
            </button>
          }
          <h2>{{ 'canvas_title_tr' | translate }}</h2>
          <span class="canvas__status">
            @if (autosaveState() === 'saving') {
              <mat-spinner diameter="14" strokeWidth="2"></mat-spinner>
            }
            <span>
              {{ autosaveState() === 'saving' ? ('canvas_saving_tr' | translate) : ('canvas_auto_saved_tr' | translate) }}
            </span>
          </span>
        </div>
        <span class="canvas__hint">
          {{ 'canvas_dropped_label_tr' | translate }}: {{ placedDocumentIds().size }} ·
          <span class="canvas__remaining-count">{{ 'canvas_remaining_label_tr' | translate }}: {{ remainingDocumentCount() }} ·</span>
          {{ 'canvas_groups_label_tr' | translate }}: {{ groups().length }}
          @if (selectionMode) {
            · {{ 'Selected_tr' | translate }}: {{ selectedDocumentIds().size }}
          }
        </span>
      </div>
      <div class="canvas__actions">
        <div class="canvas__action-row">
          @if (selectionMode && selectedDocumentIds().size > 0) {
            <button
              mat-flat-button
              color="accent"
              type="button"
              class="canvas__link-selected"
              [disabled]="readOnly()"
              (click)="linkSelectedDocuments()"
            >
              <mat-icon>attach_file</mat-icon>
              {{ 'link_Document_tr' | translate }}
            </button>
          }
          <button
            mat-flat-button
            color="primary"
            type="button"
            class="canvas__new-group"
            data-testid="canvas-new-group-button"
            [disabled]="readOnly()"
            (click)="createGroup()"
          >
            <mat-icon>folder_special</mat-icon>
            {{ 'canvas_new_group_tr' | translate }}
          </button>
          <button mat-stroked-button type="button" class="canvas__close" aria-label="Close canvas" (click)="close()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
    </header>

    <div
      id="canvas-stage-list"
      class="canvas__stage"
      cdkDropList
      [cdkDropListSortingDisabled]="true"
      [cdkDropListData]="canvasDocuments()"
      [cdkDropListConnectedTo]="['canvas-source-list']"
      [cdkDropListDisabled]="readOnly()"
      (cdkDropListDropped)="handleCanvasDrop($event)"
    >
      <div class="canvas__zoom-controls" aria-label="Canvas zoom controls">
        <button mat-icon-button type="button" aria-label="Zoom out" (click)="zoomCanvas(-0.1)">
          <mat-icon>remove</mat-icon>
        </button>
        <button mat-button type="button" class="canvas__zoom-value" (click)="resetCanvasZoom()">
          {{ canvasZoom() * 100 | number: '1.0-0' }}%
        </button>
        <button mat-icon-button type="button" aria-label="Zoom in" (click)="zoomCanvas(0.1)">
          <mat-icon>add</mat-icon>
        </button>
      </div>
      <div #surface class="canvas__surface" [style.zoom]="canvasZoom()">
        @if (!canvasDocumentsLoading()) {
          <div
            class="canvas__loose-document"
            *ngFor="let document of looseDocuments(); trackBy: trackByDocumentId"
            [style.left.px]="document.x"
            [style.top.px]="document.y"
          >
            <app-document-card
              [class.document--drop-target]="highlightedDocumentPlacementId() === document.placementId"
              [document]="document"
              variant="canvas"
              [previewSrc]="previewSrcFor(document)"
              [selectable]="selectionMode"
              [selected]="selectedDocumentIds().has(document.id)"
              [cdkDragData]="document"
              [cdkDragBoundary]="'.canvas__surface'"
              [cdkDragPreviewClass]="'canvas-document-drag-preview'"
              (pointerdown)="handleLooseDocumentPointerDown(document, $event)"
              (cdkDragStarted)="handleLooseDocumentDragStart(document, $event)"
              (cdkDragMoved)="handleCanvasDragMove($event)"
              (cdkDragEnded)="handleLooseDocumentDragEnd(document, $event)"
              (deleteDocument)="removePlacement(document.placementId)"
              (selectionChange)="toggleDocumentSelection(document.id, $event)"
            ></app-document-card>
          </div>

          <app-document-group
            *ngFor="let group of groups(); trackBy: trackByGroupId"
            [class.group--highlighted]="highlightedGroupId() === group.id"
            [group]="group"
            [documents]="documentsForGroup(group.id)"
            [previewMap]="getPreviewMap()"
            [dropActive]="!groupDragActive() && highlightedGroupId() === group.id"
            [dropListId]="dropListId(group.id)"
            [connectedDropListIds]="connectedDropListIds(group.id)"
            [dropListEnterPredicate]="documentDropPredicate"
            [selectionMode]="selectionMode"
            [selectedDocumentIds]="selectedDocumentIds()"
            [groupSelected]="isGroupSelected(group.id)"
            [groupPartial]="isGroupPartiallySelected(group.id)"
            (groupPointerStarted)="handleGroupPointerStart(group, $event)"
            (groupPointerMoved)="handleGroupPointerMove(group, $event)"
            (groupPointerEnded)="handleGroupPointerEnd(group, $event)"
            [style.left.px]="group.x"
            [style.top.px]="group.y"
            [style.width.px]="groupWidth(group)"
            [style.height.px]="groupHeight(group)"
            (rename)="renameGroup(group.id, $event)"
            (delete)="deleteGroup(group.id)"
            (groupDrop)="handleGroupDrop(group.id, $event)"
            (groupEntered)="highlightGroup($event)"
            (groupExited)="clearGroupHighlight($event)"
            (groupSelectionChange)="toggleGroupSelection(group.id, $event)"
            (documentSelectionChange)="toggleDocumentSelection($event.id, $event.selected)"
            (removeDocument)="removePlacement($event)"
            (documentDragMoved)="handleCanvasDragMove($event)"
            (moveDocument)="handleGroupedDragEnd($event)"
          ></app-document-group>
        }
        @if (highlightedDocumentPlacementId(); as placementId) {
          <div
            class="canvas__document-merge-indicator"
            [style.left.px]="documentMergeIndicatorX(placementId)"
            [style.top.px]="documentMergeIndicatorY(placementId)"
          >
            <mat-icon>south</mat-icon>
            {{ 'canvas_drop_to_create_group_tr' | translate }}
          </div>
        }
      </div>
    </div>
  </main>
</section>
